/**
 * GET-REPARTITION-CEILS
 * Build the groups displayed under the repartition widget (ceils, labels and colors)
 * NPS is frozen, QVCT and RPS Gollac scales can be customized in the settings
 */

import { isEqual } from "lodash"
import { t } from "@/translate/t"
import { Session } from "@/redux/_session.types"

export const CEILS_NPS: number[] = [6, 8]
export const CEILS_REPARTITION_DEFAULT: number[] = [6, 8]
export const CEILS_REPARTITION_GOLLAC_DEFAULT: number[] = [5]
export const CEILS_REPARTITION_MIN: number = 4
export const CEILS_REPARTITION_MAX: number = 9
export const CEILS_REPARTITION_SCALE_MAX: number = 10 //Top of the notes scale (bars are always 1 to 10)

//A group of notes displayed under the bars (min and max are inclusive notes)
export interface RepartitionGroup {
  min: number
  max: number
  label: string //Custom label, or the translated default one
  customLabel: string //Custom label only (empty if the account did not set one)
  color: string
}

export interface RepartitionOptions {
  ceilsRepartition: number[]
  ceilsRepartitionGollac: number[]
  ceilsRepartitionCustom?: boolean
  ceilsRepartitionLabels?: string[]
  ceilsRepartitionGollacLabels?: string[]
}

//Ceils were customized before the option existed => deduce it from the stored value
export function isRepartitionCustom(options: RepartitionOptions): boolean {
  return options.ceilsRepartitionCustom ?? !isEqual(options.ceilsRepartition, CEILS_REPARTITION_DEFAULT)
}

//Default label of a group, translated (a custom label is unique for every language)
export function getRepartitionDefaultLabel(index: number, count: number, isGollac: boolean, isNps: boolean): string {
  const position = index === 0 ? "unhappy" : index === count - 1 ? "veryhappy" : "happy"
  const suffix = isNps ? "_nps" : isGollac ? "_gollac" : ""
  return t("survey_repartition_" + position + suffix)
}

//Ceils and custom labels of the scale used for a survey
export function getRepartitionScale(
  options: RepartitionOptions,
  isGollac: boolean,
  isNps: boolean
): { ceils: number[]; labels: string[] } {
  if (isNps) return { ceils: CEILS_NPS, labels: [] }

  if (!isRepartitionCustom(options)) {
    return { ceils: isGollac ? CEILS_REPARTITION_GOLLAC_DEFAULT : CEILS_REPARTITION_DEFAULT, labels: [] }
  }

  return {
    ceils: isGollac ? options.ceilsRepartitionGollac : options.ceilsRepartition,
    labels: (isGollac ? options.ceilsRepartitionGollacLabels : options.ceilsRepartitionLabels) ?? []
  }
}

export function getRepartitionCeils(session: Session, isGollac: boolean, isNps: boolean): number[] {
  return getRepartitionScale(session.dashboardDisplaySettings, isGollac, isNps).ceils
}

//Red for the first group, green for the last one, yellow (NPS) or light green between
export function getRepartitionGroupColor(index: number, count: number, isNps: boolean): string {
  if (index === 0) return "#eb5a46"
  if (index === count - 1) return "#20CA7E"
  return isNps ? "#f2d600" : "#99efc2"
}

export function getRepartitionGroups(session: Session, isGollac: boolean, isNps: boolean): RepartitionGroup[] {
  const { ceils, labels } = getRepartitionScale(session.dashboardDisplaySettings, isGollac, isNps)
  const count: number = ceils.length + 1

  return [...ceils, CEILS_REPARTITION_SCALE_MAX].map((ceil: number, i: number) => ({
    min: i === 0 ? 1 : ceils[i - 1] + 1,
    max: ceil,
    label: labels[i] ? labels[i] : getRepartitionDefaultLabel(i, count, isGollac, isNps),
    customLabel: labels[i] ?? "",
    color: getRepartitionGroupColor(i, count, isNps)
  }))
}
